import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Anchor, Box, ShimmerBox } from '@common-fe/common-fe';

import HorizontalLine from '@/components/elements/HorizontalLine/HorizontalLine';

import { CombinedAccountData } from '../../AccountDetails.types';

import AccountDetailsItem from './AccountsListItem';

const INITIAL_DISPLAY_COUNT = 2;

interface Props {
  accountsData?: CombinedAccountData[];
  isLoading?: boolean;
  onReadMore?: () => void;
}

const AccountsList: React.FC<Props> = ({ accountsData, isLoading, onReadMore }) => {
  const { t } = useTranslation();

  const displayedAccounts = useMemo(
    () => (onReadMore ? accountsData?.slice(0, INITIAL_DISPLAY_COUNT) : accountsData),
    [accountsData, onReadMore]
  );

  const hiddenCount = useMemo(
    () => (accountsData?.length || 0) - INITIAL_DISPLAY_COUNT,
    [accountsData]
  );

  const showReadMoreButton = useMemo(
    () => hiddenCount > 0 && onReadMore,
    [hiddenCount, onReadMore]
  );

  if (isLoading) {
    return (
      <Box width="100%">
        <ShimmerBox height='175px' />
      </Box>
    );
  }

  return (
    <Box width="100%" data-testid="accounts-list">
      {displayedAccounts?.map((accountData, index) => (
        <React.Fragment key={accountData?.enrollment?.accountId}>
          <AccountDetailsItem
            expanded={index === 0}
            accountId={accountData?.enrollment?.accountId}
            enrollmentTitle={accountData?.enrollment?.enrollmentTitle}
            availableAmount={accountData?.enrollment?.availableAmount}
            coveragePeriods={accountData?.enrollment?.coveragePeriods}
            submitClaimsDateEnd={accountData?.enrollment?.submitClaimsDateEnd}
            categories={accountData?.policyEligibleService?.categories}
            proofOfExpensesType={accountData?.enrollment?.proofOfExpensesType}
          />
          {index !== displayedAccounts?.length && (
            <HorizontalLine margin={{ vertical: 'spacing8' }} />
          )}
        </React.Fragment>
      ))}

      {showReadMoreButton && (
        <>
          <Box pad={{ horizontal: 'spacing4', top: 'spacing2' }}>
            <Anchor
              data-testId="accounts-list_read-more"
              size="medium"
              weight={400}
              onClick={onReadMore}
            >
              {t('Read More')}{` (${hiddenCount})`}
            </Anchor>
          </Box>
        </>
      )}
    </Box>
  );
};

export default AccountsList;
